import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { activitiesFor } from "@/lib/activityRegistry";
import { GAMES, recentGames } from "@/lib/gameCatalog";
import { useAgeYears } from "@/lib/ageGroup";
import { useTierPresentation } from "@/lib/ageCopy";
import { track } from "@/lib/track";

/**
 * Homepage discovery windows — ACADEMIES · QUESTS · KNOWLEDGE TO EXPLORE · EVERYTHING.
 * Pools come from the canonical activity registry (one primaryType per activity; secondary tags allow a
 * Quest to also count as an Academy). A canonical activity is rendered ONCE on the homepage — Quests win,
 * Academies take what is left. Rotation is deterministic by day with per-device anti-repetition. Empty pools hide.
 */
const SEEN_KEY = "mwq_home_discovery_seen_v1";
function seen() { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch { return {}; } }
function remember(pool, ids) { try { const s = seen(); s[pool] = ids; localStorage.setItem(SEEN_KEY, JSON.stringify(s)); } catch { /* ignore */ } }
const dayIndex = () => Math.floor(Date.now() / 86400000);
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Deterministic daily pick of n items, preferring ones not shown last time (continuity handled by Today, not here). */
export function rotate(pool, n, poolName) {
  if (pool.length <= n) return pool;
  const last = new Set(seen()[poolName] || []);
  const start = dayIndex() % pool.length;
  const ordered = [...pool.slice(start), ...pool.slice(0, start)];
  const fresh = ordered.filter((i) => !last.has(i.activityId));
  return [...fresh, ...ordered.filter((i) => last.has(i.activityId))].slice(0, n);
}

const KIND = { QUEST: "Quest", CHALLENGE: "Challenge", STORY: "Story", ACADEMY: "Academy" };
const questKind = (a) => a.secondaryTags.includes("KNOWLEDGE") ? "Knowledge quest" : a.secondaryTags.includes("AI") ? "Deep quest" : a.secondaryTags.includes("CODE") ? "Creative quest" : a.secondaryTags.includes("MATHS") ? "Skill quest" : KIND[a.primaryType] || "Quest";

/** One section header for the whole homepage: title + sub on the left, optional "more" on the right, baseline-aligned. */
export function SectionHead({ id, title, sub, more, moreTestId, dot = false }) {
  return (
    <div className="home-sec-head flex items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 id={id} className="mwq-card-title text-[1.15rem] md:text-[1.35rem] flex items-center gap-2">
          {dot && <span className="relative inline-flex h-2.5 w-2.5 shrink-0" aria-hidden><span className="absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75 motion-safe:animate-ping" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-600" /></span>}
          {title}
        </h2>
        {sub && <p className="mwq-meta mt-0.5">{sub}</p>}
      </div>
      {more && <Link to={more.to} className="mwq-btn mwq-btn-tertiary text-sm shrink-0 whitespace-nowrap" data-testid={moreTestId}>{more.label}</Link>}
    </div>
  );
}

/** Uniform discovery card: emoji tile · eyebrow · title · 2-line blurb. Equal heights come from the grid. */
export function DiscoveryCard({ to, onClick, testId, tint = "", emoji, art, eyebrow: eb, title, blurb }) {
  return (
    <Link to={to} onClick={onClick} data-testid={testId} className={`mwq-card ${tint} h-full flex items-start gap-3 p-3`}>
      {art ? <img src={art} alt="" loading="lazy" width="56" height="56" className="h-12 w-12 md:h-14 md:w-14 shrink-0 rounded-xl object-cover border-2 border-[var(--color-border)]" />
        : emoji ? <span aria-hidden className="h-12 w-12 md:h-14 md:w-14 shrink-0 grid place-items-center rounded-xl bg-white/70 border-2 border-[var(--color-border)] text-2xl md:text-3xl leading-none">{emoji}</span> : null}
      <span className="min-w-0 flex-1">
        {eb && <span className="block mwq-meta text-[11px] uppercase tracking-wide">{eb}</span>}
        <span className="block font-extrabold text-[var(--color-text)] leading-tight">{title}</span>
        {blurb && <span className="mwq-meta mt-1 line-clamp-2">{blurb}</span>}
      </span>
    </Link>
  );
}

function Rail({ id, title, sub, items, more, render, testId }) {
  useEffect(() => { track(`${id}_section_viewed`, { count: items.length }); }, [id, items.length]);
  if (!items.length) return null;
  return (
    <section className="mt-7 md:mt-9" data-testid={testId} aria-labelledby={`${testId}-title`}>
      <SectionHead id={`${testId}-title`} title={title} sub={sub} more={more} moreTestId={`${testId}-more`} />
      <ul className={`mt-3 grid gap-2.5 md:gap-3 ${items.length === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`} role="list">
        {items.map((it) => <li key={it.activityId} className="min-w-0">{render(it)}</li>)}
      </ul>
    </section>
  );
}

// Learning-OS framing copy — kept for Grown-up surfaces / docs; no longer rendered on the child homepage (Oct 2026).
export const DISCOVERY_FRAME_COPY = "Different doors, one learning record — every academy, quest, topic, game and story below feeds the same Learn → Pathway → Subject → Topic → Lesson → Practice → Check → Evidence → Next-step journey.";
// Display rule: hide an eyebrow that merely repeats the card title ("Quest" over "First Quest").
const eyebrow = (label, title) => (label && String(title || "").toLowerCase().includes(String(label).toLowerCase()) ? null : label);

export default function HomeDiscoveryWorlds() {
  const { tier, emoji } = useTierPresentation();
  const n = tier === "seedlings" ? 3 : 4;
  const age = useAgeYears() || 8;
  // PLAY — canonical games (same ids/routes as /games): recently played first, then a daily rotation for this age.
  const play = useMemo(() => {
    const fits = (g) => (g.ageMin ?? 4) <= age && (g.ageMax ?? 15) >= age;
    const recent = recentGames(2).filter(fits);
    const rest = GAMES.filter((g) => fits(g) && !recent.some((r) => r.id === g.id));
    return [...recent.map((g) => ({ ...g, recent: true })), ...rotate(rest.map((g) => ({ ...g, activityId: g.id })), n - recent.length, "play")].slice(0, n);
  }, [age, n]);
  const { quests, academies, knowledge, create } = useMemo(() => {
    const used = new Set();
    const take = (pool, count, name) => {
      const local = new Set();
      const uniq = pool.filter((a) => {
        const t = norm(a.title);
        if (used.has(a.canonicalId) || used.has(t) || local.has(a.canonicalId) || local.has(t)) return false;
        local.add(a.canonicalId); local.add(t); return true;
      });
      const picked = rotate(uniq, count, name);
      picked.forEach((a) => { used.add(a.canonicalId); used.add(norm(a.title)); });
      remember(name, picked.map((a) => a.activityId));
      return picked;
    };
    const q = take(activitiesFor({ primaryType: "QUEST", tier }), n, "quests");
    const a = take([...activitiesFor({ primaryType: "ACADEMY", tier }), ...activitiesFor({ tag: "ACADEMY", tier })], n, "academies");
    const k = take(activitiesFor({ primaryType: "KNOWLEDGE", tier }), tier === "seedlings" ? 4 : 8, "knowledge");
    const c = take([...activitiesFor({ primaryType: "CREATIVE", tier }), ...activitiesFor({ primaryType: "STORY", tier }), ...activitiesFor({ primaryType: "READING", tier })], n, "create");
    return { quests: q, academies: a, knowledge: k, create: c };
  }, [tier, n]);
  const open = (kind, id, to) => () => { track(`${kind}_opened`, { id, tier }); track("discovery_item_clicked", { kind, id, to }); };
  return (
    <div data-testid="home-discovery-worlds">
      <div data-testid="home-discovery-frame" aria-hidden />
      <Rail id="academy" testId="home-academies" title="Academies" sub="Go deeper. Learn step by step." items={academies} more={{ label: "All academies", to: "/everything?type=academy" }}
        render={(r) => <DiscoveryCard to={r.route} onClick={open("academy", r.activityId, r.route)} testId={`home-academy-${r.activityId}`} tint="mwq-card-tint-primary" emoji={r.emoji || "🎓"} title={r.title} blurb={r.description} />} />
      <Rail id="quest" testId="home-quests" title="Quests" sub="Choose your next adventure." items={quests} more={{ label: "All quests", to: "/everything?type=quest" }}
        render={(q) => <DiscoveryCard to={q.route} onClick={open("quest", q.activityId, q.route)} testId={`home-quest-${q.activityId}`} tint="mwq-card-tint-reward" emoji={q.emoji || "🗺️"} eyebrow={eyebrow(questKind(q), q.title)} title={q.title} blurb={q.description} />} />
      {knowledge.length > 0 && (
        <section className="mt-7 md:mt-9" data-testid="home-knowledge" aria-labelledby="home-knowledge-title">
          <SectionHead id="home-knowledge-title" title="Knowledge to explore" sub="What are you curious about?" />
          <ul className={`mt-3 grid gap-2.5 md:gap-3 ${tier === "seedlings" ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"}`} role="list">
            {knowledge.map((k) => <li key={k.activityId}><Link to={k.route} onClick={open("knowledge", k.activityId, k.route)} data-testid={`home-knowledge-${k.activityId}`} className="mwq-card flex items-center gap-2 min-h-[56px] py-2 px-3">{(emoji || tier !== "pioneers") && <span aria-hidden className="text-2xl">{k.emoji}</span>}<span className="font-extrabold text-sm text-[var(--color-text)]">{k.title}</span></Link></li>)}
          </ul>
        </section>
      )}
      <Rail id="play" testId="home-play" title="Play" sub="Practise and have fun — pick a game." items={play} more={{ label: "All games", to: "/games" }}
        render={(g) => <DiscoveryCard to={g.to} onClick={open("play", g.id, g.to)} testId={`home-play-${g.id}`} tint="mwq-card-tint-creative" art={g.art} emoji={g.emoji || "🎮"} eyebrow={g.recent ? "Continue playing" : (g.isNew ? "New" : (g.subjects || []).filter((x) => x !== "mixed").join(" · ") || "Game")} title={g.label} blurb={g.tagline} />} />
      <Rail id="create" testId="home-create" title="Create + Read" sub="Make something. Read something." items={create} more={{ label: "More", to: "/create" }}
        render={(c) => <DiscoveryCard to={c.route} onClick={open("create", c.activityId, c.route)} testId={`home-create-${c.activityId}`} tint="mwq-card-tint-success" emoji={c.emoji || (c.primaryType === "CREATIVE" ? "🎨" : "📖")} eyebrow={eyebrow(c.primaryType === "CREATIVE" ? "Create" : c.primaryType === "STORY" ? "Story" : "Read", c.title)} title={c.title} blurb={c.description} />} />
      <section className="mt-7 md:mt-9 mwq-card mwq-card-tint-success flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="home-everything" aria-labelledby="home-everything-title">
        <div>
          <h2 id="home-everything-title" className="mwq-card-title">There's a lot to explore</h2>
          <p className="mwq-meta mt-0.5">Learning · Quests · Games · Stories · Discoveries · Creation</p>
        </div>
        <Link to="/everything" onClick={() => track("everything_opened", { from: "home" })} data-testid="home-everything-cta" className="mwq-btn mwq-btn-secondary shrink-0">Explore everything →</Link>
      </section>
    </div>
  );
}
